import { isChildOfTag, isElementNode, isDocumentNode } from 'anodum';

class ReferenceMap {
  constructor(options) {
    this.defaults = {
      referenceAttribute: 'data-uuid',
    };

    this.options = {
      ...this.defaults,
      ...options,
    };

    this.map = {};
    this.virtualMap = {};
    this.referenceCounter = 1;
  }

  addReference(liveNode, staticNode) {
    if (!isElementNode(liveNode)) throw new Error('liveNode is not an ElementNode');

    // Ignore SVG internal, since we don't such granular editing
    if (isChildOfTag(liveNode, 'svg')) return;

    const referenceId = `ref_${this.referenceCounter}`;

    // Set up a reference between live and static nodes
    this.map[referenceId] = staticNode;
    // Reference attribute is required in order to differ static and dynamically added elements.
    liveNode.setAttribute(this.options.referenceAttribute, referenceId);
    this.referenceCounter += 1;
  }

  addVirtualReference(staticNode) {
    if (!staticNode) {
      throw new Error('staticNode is not defined');
    }

    const referenceId = `ref_${this.referenceCounter}`;
    this.virtualMap[referenceId] = staticNode;
    this.referenceCounter += 1;
  }

  isReferenceId(referenceId) {
    return (
      Object.prototype.hasOwnProperty.call(this.map, referenceId) ||
      Object.prototype.hasOwnProperty.call(this.virtualMap, referenceId)
    );
  }

  getReferenceById(referenceId) {
    if (!this.isReferenceId(referenceId)) return null;
    return this.map[referenceId] || this.virtualMap[referenceId];
  }

  getReference(liveNode) {
    const id = this.getReferenceId(liveNode);
    return this.getReferenceById(id);
  }

  getReferenceId(liveNode) {
    if (!isElementNode(liveNode)) return null;
    return liveNode.getAttribute(this.options.referenceAttribute);
  }

  getReferenceIdByStaticNode(staticNode) {
    if (!isElementNode(staticNode)) return null;

    const nodeId = Object.keys(this.map).find((id) => this.map[id] === staticNode);

    if (nodeId) {
      return nodeId;
    }

    const virtualNodeId = Object.keys(this.virtualMap).find(
      (id) => this.virtualMap[id] === staticNode
    );

    return virtualNodeId || null;
  }

  getPreviousReferenceId(liveNode) {
    let referenceId = null;
    let node = liveNode.previousElementSibling;
    while (node) {
      referenceId = this.getReferenceId(node);
      if (referenceId) break;
      node = node.previousElementSibling;
    }

    return referenceId;
  }

  getNextReferenceId(liveNode) {
    let referenceId = null;
    let node = liveNode.nextElementSibling;
    while (node) {
      referenceId = this.getReferenceId(node);
      if (referenceId) break;
      node = node.nextElementSibling;
    }

    return referenceId;
  }

  getParentReferenceId(liveNode) {
    let referenceId = null;
    let node = liveNode.parentElement;
    while (node) {
      referenceId = this.getReferenceId(node);
      if (referenceId) break;
      node = node.parentElement;
    }

    return referenceId;
  }

  getFirstChildReferenceId(liveNode) {
    let referenceId = null;
    let node = liveNode.firstElementChild;
    while (node) {
      referenceId = this.getReferenceId(node);
      if (referenceId) break;
      node = node.nextElementSibling;
    }

    return referenceId;
  }

  getLastChildReferenceId(liveNode) {
    let referenceId = null;
    let node = liveNode.lastElementChild;
    while (node) {
      referenceId = this.getReferenceId(node);
      if (referenceId) break;
      node = node.previousElementSibling;
    }

    return referenceId;
  }

  getClosestReferenceId(liveNode) {
    return this.getReferenceId(liveNode) || this.getParentReferenceId(liveNode);
  }

  hasReference(liveNode) {
    return this.isReferenceId(this.getReferenceId(liveNode));
  }

  removeReference(liveNode) {
    const referenceId = this.getReferenceId(liveNode);

    liveNode.removeAttribute(this.options.referenceAttribute);
    this.removeFromMap(referenceId);
  }

  removeFromMap(referenceId) {
    delete this.map[referenceId];
  }

  reset() {
    this.map = {};
    this.virtualMap = {};
    this.referenceCounter = 1;
  }

  flush() {
    Object.keys(this.map).forEach((id) => {
      let node = this.map[id];

      while (node && !isDocumentNode(node)) {
        if (!node.parentNode) {
          delete this.map[id];
          return;
        }
        node = node.parentNode;
      }
    });
  }
}

export default ReferenceMap;
